import { useAuthStore, useDrawerStore } from '../../../store';
import {
  useCreateData,
  useFetchData,
  useFetchFile,
  useProductStore,
  useUpdateData,
} from '../../../hooks';
import React, { useEffect, useState } from 'react';
import { BaseModel, FormField, PageableResponse } from '../../../types';
import {
  AuditStatus,
  ProductSource,
  ProductStatus,
  Resource,
  SupplyMethod,
} from '../../../lib/enums';
import { Spin } from 'antd';
import FormDrawer from '../../../components/ui/FormDrawer';
import AttachmentUpload from '../../../components/AttachmentUpload';
import { isUserCanEdit } from '../../../lib/utils';
import ErrorModal from '../../../components/ui/modal/ErrorModal';

const ProductForm = ({
  api,
  moduleName,
}: {
  api: string;
  moduleName: string;
}) => {
  const drawerStore = useDrawerStore();
  const { user } = useAuthStore();
  const productStore = useProductStore();
  const [spinning, setSpinning] = useState<boolean>(false);
  const [isInfoModalOpen, setInfoModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>('');

  // Attachments belong to a saved product; there is nothing to attach to while
  // one is being created, so the fetch waits for an id.
  const editingId = productStore.data?.id;
  const { data: attachments } = useFetchFile(
    `${api}/${editingId}/files`,
    'api/product/products',
    undefined,
    { enabled: Boolean(editingId) },
  );

  const {
    mutate,
    isSuccess: isSuccessCreate,
    isError: isErrorCreate,
    error: errorCreate,
  } = useCreateData(api);
  const {
    mutate: mutateUpdate,
    isSuccess: isSuccessUpdate,
    isError: isErrorUpdate,
    error: errorUpdate,
  } = useUpdateData(api);

  const { data: baseUomData, isLoading: isbaseUomLoading } = useFetchData<
    PageableResponse<BaseModel>
  >('/product/uoms', {
    resource: Resource.MIN,
    search: '',
    length: '999999',
  });

  // const { data: categoryData, isLoading: isCategoryLoading } = useFetchData<
  //   PageableResponse<BaseModel>
  // >('/product/categories', {
  //   resource: Resource.MIN,
  //   search: '',
  //   length: '999999',
  // });

  useEffect(() => {
    if (isSuccessCreate || isSuccessUpdate || isErrorUpdate || isErrorCreate)
      setSpinning(false);

    if (isErrorUpdate) {
      setInfoModalOpen(true);
      setErrorMessage(errorUpdate?.response?.data.message);
    }
    if (isErrorCreate) {
      setInfoModalOpen(true);
      setErrorMessage(errorCreate?.response?.data.message);
    }
  }, [
    isSuccessCreate,
    isSuccessUpdate,
    isErrorUpdate,
    isErrorCreate,
    errorCreate,
  ]);

  const supplyMethodOptions: BaseModel[] = Object.keys(SupplyMethod).map(
    (key) =>
      ({
        id: key,
        code: SupplyMethod[key as keyof typeof SupplyMethod],
        description: SupplyMethod[key as keyof typeof SupplyMethod],
      }) as BaseModel,
  );

  const productStatusOptions: BaseModel[] = Object.keys(ProductStatus).map(
    (key) =>
      ({
        id: key,
        code: ProductStatus[key as keyof typeof ProductStatus],
        description: ProductStatus[key as keyof typeof ProductStatus],
      }) as BaseModel,
  );

  const productSourceOptions: BaseModel[] = Object.keys(ProductSource).map(
    (key) =>
      ({
        id: key,
        code: ProductSource[key as keyof typeof ProductSource],
        description: ProductSource[key as keyof typeof ProductSource],
      }) as BaseModel,
  );

  const productAuditStatusOptions: BaseModel[] = Object.keys(AuditStatus).map(
    (key) =>
      ({
        id: key,
        code: AuditStatus[key as keyof typeof AuditStatus],
        description: AuditStatus[key as keyof typeof AuditStatus],
      }) as BaseModel,
  );

  const onFinish = (values: {
    defaultUom: string;
    purchaseUom: string;
    productCategory: string;
  }) => {
    setSpinning(true);
    const defaultUom: BaseModel | undefined = baseUomData?.data?.find((obj) => {
      return obj.id == values.defaultUom;
    });
    const purchaseUom: BaseModel | undefined = baseUomData?.data?.find(
      (obj) => {
        return obj.id == values.purchaseUom;
      },
    );
    const category: BaseModel | undefined = baseUomData?.data?.find((obj) => {
      return obj.id == values.productCategory;
    });

    const jsonData = {
      ...values,
      defaultUom: defaultUom,
      productCategory: category,
      purchaseUom: purchaseUom,
    };
    if (productStore.data !== null) {
      const updatedData = {
        ...productStore.data,
        ...jsonData,
      };
      mutateUpdate({ id: productStore.data.id, form: updatedData });
    } else {
      mutate(jsonData);
    }
  };

  const formFields: FormField[] = [
    {
      name: 'code',
      label: 'Code',
      rules: [{ required: true, message: 'Please enter Code' }],
      disabled: productStore.data != null,
      defaultValue: productStore.data?.code,
    },
    {
      name: 'description',
      label: 'Description',
      rules: [{ required: true, message: 'Please enter description' }],
      defaultValue: productStore.data?.description,
    },
    {
      name: 'purchaseUom',
      label: 'Purchase UoM',
      isMultiple: false,
      type: 'select',
      allowClear: true,
      isLoading: isbaseUomLoading,
      values: baseUomData?.data,
      defaultValue: productStore.data?.defaultUom?.id,
    },
    {
      name: 'defaultUom',
      label: 'Default UoM',
      isMultiple: false,
      type: 'select',
      allowClear: true,
      isLoading: isbaseUomLoading,
      values: baseUomData?.data,
      defaultValue: productStore.data?.defaultUom?.id,
    },
    // {
    //   name: 'productCategory',
    //   label: 'Category',
    //   isMultiple: false,
    //   type: 'select',
    //   allowClear: true,
    //   values: categoryData?.data,
    //   isLoading: isCategoryLoading,
    //   defaultValue: productStore.data?.productCategory?.id,
    // },
    {
      name: 'supplyMethod',
      label: 'Supply Method',
      isMultiple: false,
      type: 'select',
      allowClear: true,
      values: supplyMethodOptions,
      defaultValue: productStore.data?.supplyMethod,
    },
    {
      name: 'productStatus',
      label: 'Product Status',
      isMultiple: false,
      type: 'select',
      allowClear: true,
      values: productStatusOptions,
      defaultValue: productStore.data?.productStatus,
    },
    {
      name: 'productSource',
      label: 'Product Source',
      isMultiple: false,
      type: 'select',
      allowClear: true,
      values: productSourceOptions,
      defaultValue: productStore.data?.productSource,
    },
    {
      name: 'auditStatus',
      label: 'Audit Status',
      isMultiple: false,
      type: 'select',
      allowClear: true,
      values: productAuditStatusOptions,
      defaultValue: productStore.data?.auditStatus,
    },
    // {
    //   name: 'canPurchase',
    //   label: 'Can Purchase',
    //   type: 'switch',
    //   defaultValue: productStore.data?.canPurchase,
    // },
    // {
    //   name: 'canSold',
    //   label: 'Can Sold',
    //   type: 'switch',
    //   defaultValue: productStore.data?.canSold,
    // },
    {
      name: 'width',
      label: 'Width',
      defaultValue: productStore.data?.width,
      type: 'numeric',
    },
    {
      name: 'length',
      label: 'Length',
      defaultValue: productStore.data?.length,
      type: 'numeric',
    },
    {
      name: 'height',
      label: 'Height',
      defaultValue: productStore.data?.height,
      type: 'numeric',
    },
    {
      name: 'weight',
      label: 'Weight',
      defaultValue: productStore.data?.weight,
      type: 'numeric',
    },
    {
      name: 'purchaseCost',
      label: 'Purchase Cost',
      defaultValue: productStore.data?.purchaseCost,
      type: 'numeric',
    },
    {
      name: 'salePrice',
      label: 'Sale Price',
      defaultValue: productStore.data?.salePrice,
      type: 'numeric',
    },
  ];
  return (
    <>
      <Spin spinning={spinning} fullscreen />
      <FormDrawer
        size={'large'}
        isOpen={drawerStore.isOpen}
        onClose={drawerStore.onClose}
        data={productStore.data}
        moduleName={moduleName}
        setData={productStore.setData}
        onFinish={onFinish}
        formFields={formFields}
      >
        {editingId && (
          <AttachmentUpload
            label={'Attachments'}
            attachments={attachments}
            api={api}
            recordId={editingId}
            canEdit={isUserCanEdit(user?.moduleAccess, 'Products')}
          />
        )}
      </FormDrawer>
      <ErrorModal
        visible={isInfoModalOpen}
        message={errorMessage}
        onClose={() => setInfoModalOpen(false)}
      />
    </>
  );
};

export default ProductForm;
